import { useEffect, useMemo } from 'react';
import log from 'loglevel';
import { useForm } from '../../../../../shared/form/useForm';
import { IExtrasFormData } from '../IExtrasFormData';
import { useExtrasSchema } from './useExtrasSchema';
import { IAncillaryGroupSelection } from '../../../../domain/ancillary/domain/models/IAncillaryGroupSelection';
import { useNewBookingStore } from '../../../../domain/booking/stores/useNewBookingsStore';

export function useExtrasForm() {
	const newBookingStore = useNewBookingStore();

	const defaultValues = useMemo(
		() =>
			getDefaultValuesForSelections(newBookingStore.ancillaryGroupSelections),
		[newBookingStore.ancillaryGroupSelections],
	);

	const validationSchema = useExtrasSchema();
	const form = useForm<IExtrasFormData>({
		validationSchema,
		defaultValues,
	});

	useEffect(() => {
		form.reset(defaultValues);
	}, [defaultValues]);

	return form;
}

function getDefaultValuesForSelections(
	ancillaryGroupSelections: IAncillaryGroupSelection[],
) {
	const ancillaryGroups = ancillaryGroupSelections.map(
		(ancillaryGroupSelection) => {
			// If there are no selected ancillaries, we pre-select the first ancillary to ensure the form is always in a valid state.
			const defaultSelection = ancillaryGroupSelection.ancillaries[0]
				? [ancillaryGroupSelection.ancillaries[0].id]
				: [];
			return {
				...ancillaryGroupSelection,
				selected: adaptSelected(
					ancillaryGroupSelection.selected.length > 0
						? ancillaryGroupSelection.selected
						: defaultSelection,
				),
				ancillaries: ancillaryGroupSelection.ancillaries?.map((ancillary) => ({
					...ancillary,
					extras: ancillary.extras?.map((extra) => ({
						...extra,
						selected: adaptSelected(extra.selected) ?? '',
					})),
				})),
			};
		},
	);

	// We need to do some casting here to get this to work properly with react-hook-forms, see details in functions below
	const defaultValues: unknown = { ancillaryGroups };

	log.info('Init Extras Form with default values', defaultValues);

	return defaultValues as IExtrasFormData;
}

// Only string values will be matched against input values, so IDs need to be mapped to strings
function adaptSelected(selectedIds: number[]) {
	const stringIds = selectedIds.map((id) => id.toString());

	return adaptSingleItem(stringIds);
}

// Arrays with single item need to be mapped to a single item to support pre-selection of radio input
function adaptSingleItem<T>(oneOrMultipleItems: T[]): T[] | T | undefined {
	if (oneOrMultipleItems.length === 0) {
		return undefined;
	}

	if (oneOrMultipleItems.length === 1) {
		return oneOrMultipleItems[0];
	}

	return oneOrMultipleItems;
}
